import eventBus from './EventBus';

class HistoryManager {
  constructor(canvasManager, maxSteps = 50) {
    this.canvasManager = canvasManager;
    this.maxSteps = maxSteps;
    this.undoStack = [];
    this.redoStack = [];
    this._isRestoring = false;
    this._initialized = false;
    this._saveTimeout = null;
    this._saveDelay = 300; // ms debounce to prevent rapid-fire saves
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;
    this.saveState();

    eventBus.on('canvas:changed', () => {
      if (!this._isRestoring) {
        // Debounce: many events fire in rapid succession (e.g. overlay redraw)
        if (this._saveTimeout) clearTimeout(this._saveTimeout);
        this._saveTimeout = setTimeout(() => {
          this.saveState();
        }, this._saveDelay);
      }
    });
  }

  /**
   * Lightweight canvas serialization for history (skips overlay objects and
   * expensive image-to-dataURL conversion that getJSON does).
   */
  _getHistoryJSON() {
    const canvas = this.canvasManager.canvas;
    if (!canvas) return null;
    // Use toJSON with custom properties but skip the heavy getJSON wrapper
    const json = canvas.toJSON([
      'customName', 'isLocked', 'isGuide', 'guideOrientation', 'guidePositionPx',
      'excludeFromExport',
      '_curvedText', '_curvedType', '_curvedFontSize', '_curvedFontFamily',
      '_curvedFontWeight', '_curvedFill', '_curvedStroke', '_curvedStrokeWidth',
      '_curvedRadiusFactor', '_curvedSpacing', '_curvedCharSpacing', '_curvedLineHeight',
      // Overlay flags so we can filter them out
      'isGrid', 'isBleed', 'isHem', 'isHohlsaum', 'isGrommet', 'isSnapLine',
    ]);
    // Remove overlay/transient objects from the snapshot — they are redrawn on restore
    // CRITICAL: also filter snap lines + guides so transient drag overlays do NOT pollute history
    if (json.objects) {
      json.objects = json.objects.filter(
        (o) => !o.isGrid && !o.isBleed && !o.isHem && !o.isHohlsaum &&
               !o.isGrommet && !o.isSnapLine && !o.isGuide
      );
    }
    return json;
  }

  saveState() {
    if (!this.canvasManager.canvas) return;
    const json = JSON.stringify(this._getHistoryJSON());
    if (this.undoStack.length > 0 && this.undoStack[this.undoStack.length - 1] === json) {
      return;
    }
    this.undoStack.push(json);
    if (this.undoStack.length > this.maxSteps) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this._emitChange();
  }

  async undo() {
    if (!this.canUndo()) return;
    // Cancel any pending debounced save
    if (this._saveTimeout) { clearTimeout(this._saveTimeout); this._saveTimeout = null; }
    this._isRestoring = true;
    try {
      const current = this.undoStack.pop();
      this.redoStack.push(current);
      const previous = this.undoStack[this.undoStack.length - 1];
      await this._restoreState(JSON.parse(previous));
    } finally {
      this._isRestoring = false;
      this._emitChange();
    }
  }

  async redo() {
    if (!this.canRedo()) return;
    if (this._saveTimeout) { clearTimeout(this._saveTimeout); this._saveTimeout = null; }
    this._isRestoring = true;
    try {
      const next = this.redoStack.pop();
      this.undoStack.push(next);
      await this._restoreState(JSON.parse(next));
    } finally {
      this._isRestoring = false;
      this._emitChange();
    }
  }

  /**
   * Fast canvas restore for undo/redo. Skips font preloading (fonts are already
   * loaded in this session) and image URL validation. Uses loadFromJSON directly.
   */
  async _restoreState(json) {
    const cm = this.canvasManager;
    const canvas = cm.canvas;
    if (!canvas) return;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[HistoryManager] restore timeout');
        cm._redrawOverlays();
        resolve();
      }, 5000);

      canvas.loadFromJSON(json, () => {
        clearTimeout(timeout);

        // Disable inline editing for all text objects (CSS zoom conflict)
        canvas.getObjects().forEach(obj => {
          if (obj._curvedText && obj.type === 'group') {
            obj.set('hasControls', true);
            obj.set('lockUniScaling', false);
          }
          if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') {
            obj.set('editable', false);
          }
        });

        // Redraw overlays (bleed, hem, grommets) which were stripped from the snapshot
        cm._redrawOverlays();
        canvas.renderAll();
        eventBus.emit('layers:changed');
        resolve();
      });
    });
  }

  canUndo() {
    return this.undoStack.length > 1;
  }

  canRedo() {
    return this.redoStack.length > 0;
  }

  _emitChange() {
    eventBus.emit('history:changed', {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoCount: this.undoStack.length - 1,
      redoCount: this.redoStack.length,
    });
  }

  clear() {
    if (this._saveTimeout) { clearTimeout(this._saveTimeout); this._saveTimeout = null; }
    this.undoStack = [];
    this.redoStack = [];
    this.saveState();
  }
}

export default HistoryManager;
